import { useCallback, useState } from 'react';
import { groupsService } from '@wlb/shared/api/groups';
import { GROUP_ROLE, type GroupRoleType } from '@wlb/shared/api/groups/groupsTypes';
import type { GroupActivity } from '@wlb/shared/types';
import type { GroupMember, PendingApprovalRequest } from '@wlb/shared/types/groupDetails';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from '../../components/ui/Button';
import { DataTable, type DataTableColumn } from '../../components/ui/DataTable';
import { DetailList } from '../../components/ui/DetailList';
import { FormError } from '../../components/ui/FormError';
import { IconButton } from '../../components/ui/IconButton';
import { Modal } from '../../components/ui/Modal';
import { SelectField } from '../../components/ui/SelectField';
import { StateBlock } from '../../components/ui/StateBlock';
import { StatusPill } from '../../components/ui/StatusPill';
import { useAsyncResource } from '../../hooks/useAsyncResource';
import { formatCount, formatDate } from '../../utils/format';
import '../../components/ui/ui.css';

interface GroupDetailsDialogProps {
  groupId: string;
  groupName: string;
  onClose: () => void;
  /** Called after a change that alters the group list (delete, membership). */
  onChanged: () => void;
}

interface Bundle {
  members: GroupMember[];
  requests: PendingApprovalRequest[];
  totalMembers: number;
  privacyType: string;
  description: string;
  createdDate: string;
  ownerName: string;
  activity: GroupActivity;
}

const ROLE_OPTIONS: readonly { value: GroupRoleType; labelKey: string }[] = [
  { value: GROUP_ROLE.Owner, labelKey: 'groups.roles.owner' },
  { value: GROUP_ROLE.Admin, labelKey: 'groups.roles.admin' },
  { value: GROUP_ROLE.Member, labelKey: 'groups.roles.member' },
];

/**
 * Group moderation in one place: who is in the group, who is waiting to join,
 * and the last resort of removing the group. Join requests only exist for
 * private groups, so an empty list there is normal rather than an error.
 */
export function GroupDetailsDialog({
  groupId,
  groupName,
  onClose,
  onChanged,
}: GroupDetailsDialogProps) {
  const { t } = useTranslation('admin');
  const [pending, setPending] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async (): Promise<Bundle> => {
    const [detail, requests] = await Promise.all([
      groupsService.getGroupDetails(groupId),
      groupsService.getGroupJoinRequests(groupId),
    ]);
    return {
      members: detail.members,
      requests: requests.data,
      totalMembers: detail.data.totalMembers ?? detail.members.length,
      privacyType: detail.data.privacyType ?? '',
      description: detail.data.groupDescription ?? '',
      createdDate: detail.data.createdDate ?? '',
      ownerName: detail.data.admin?.name ?? '',
      activity: detail.data.activity,
    };
  }, [groupId]);

  /* Deliberately uncached: membership changes as the operator works, and a
     reopened dialog must never show the roster it had before a removal. */
  const { data, status, refreshing, error, reload } = useAsyncResource(
    load,
    groupId,
    t('common.errorTitle'),
  );

  /** Resolves `false` when the action failed, so callers can keep the dialog
   *  open on the error banner instead of closing over it. */
  const run = async (
    action: () => Promise<unknown>,
    successMessage: string,
  ): Promise<boolean> => {
    setActionError(null);
    setPending(true);
    try {
      await action();
      ToastService.show({ type: 'success', message: successMessage });
      reload();
      onChanged();
      return true;
    } catch (cause) {
      setActionError(getUserFacingErrorMessage(cause, t('common.errorTitle')));
      return false;
    } finally {
      setPending(false);
    }
  };

  const memberUserId = (member: GroupMember): number => Number(member.userId ?? member.id);

  const memberColumns: readonly DataTableColumn<GroupMember>[] = [
    {
      key: 'member',
      header: t('groups.members.columns.member'),
      render: member => (
        <>
          <span className="ui-table__primary">{member.name || member.username}</span>
          {member.username ? (
            <span className="ui-table__secondary">{member.username}</span>
          ) : null}
        </>
      ),
    },
    {
      key: 'joined',
      header: t('groups.members.columns.joined'),
      render: member => formatDate(member.joinedDate) || t('common.none'),
    },
    {
      key: 'role',
      header: t('groups.members.columns.role'),
      render: member => (
        <SelectField
          label={t('groups.members.roleFor', {
            name: member.name || member.username || '',
          })}
          labelHidden
          value={
            member.isOwner
              ? GROUP_ROLE.Owner
              : member.isAdmin
                ? GROUP_ROLE.Admin
                : GROUP_ROLE.Member
          }
          disabled={pending}
          options={ROLE_OPTIONS.map(option => ({
            value: option.value,
            label: t(option.labelKey),
          }))}
          onChange={event =>
            run(
              () =>
                groupsService.changeMemberRole(
                  groupId,
                  memberUserId(member),
                  event.target.value as GroupRoleType,
                ),
              t('groups.toasts.roleChanged'),
            )
          }
        />
      ),
    },
    {
      key: 'approval',
      header: t('groups.members.columns.approval'),
      render: member =>
        member.approvalStatus || member.approvedBy ? (
          <>
            <span className="ui-table__primary">
              {member.approvalStatus || t('common.none')}
            </span>
            {member.approvedBy ? (
              <span className="ui-table__secondary">
                {t('groups.members.approvedBy', { name: member.approvedBy })}
              </span>
            ) : null}
          </>
        ) : (
          t('common.none')
        ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      render: member => (
        <div className="ui-table__actions">
          <IconButton
            icon="userX"
            tone="rose"
            label={t('groups.members.remove')}
            disabled={pending}
            onClick={() =>
              run(
                () => groupsService.removeMember(groupId, memberUserId(member)),
                t('groups.toasts.memberRemoved'),
              )
            }
          />
        </div>
      ),
    },
  ];

  const requestColumns: readonly DataTableColumn<PendingApprovalRequest>[] = [
    {
      key: 'name',
      header: t('groups.requests.columns.member'),
      render: request => <span className="ui-table__primary">{request.name}</span>,
    },
    {
      key: 'invitedBy',
      header: t('groups.requests.columns.invitedBy'),
      render: request => request.invitedBy || t('common.none'),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      render: request => (
        <div className="ui-table__actions">
          <IconButton
            icon="check"
            tone="emerald"
            label={t('groups.requests.approve')}
            disabled={pending}
            onClick={() =>
              run(
                () =>
                  groupsService.reviewJoinRequest(request.groupJoinRequestId, 'Approve'),
                t('groups.toasts.requestApproved'),
              )
            }
          />
          <IconButton
            icon="close"
            tone="rose"
            label={t('groups.requests.reject')}
            disabled={pending}
            onClick={() =>
              run(
                () =>
                  groupsService.reviewJoinRequest(request.groupJoinRequestId, 'Reject'),
                t('groups.toasts.requestRejected'),
              )
            }
          />
        </div>
      ),
    },
  ];

  if (confirmDelete) {
    return (
      <Modal
        open
        title={t('groups.deleteDialog.title')}
        description={groupName}
        onClose={() => setConfirmDelete(false)}
        footer={
          <>
            <Button
              variant="secondary"
              disabled={pending}
              onClick={() => setConfirmDelete(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              disabled={pending}
              onClick={async () => {
                const deleted = await run(
                  () => groupsService.deleteGroup(groupId),
                  t('groups.toasts.deleted'),
                );
                if (!deleted) {
                  return;
                }
                setConfirmDelete(false);
                onClose();
              }}>
              {t('groups.deleteDialog.confirm')}
            </Button>
          </>
        }>
        <p>{t('groups.deleteDialog.description')}</p>
        <FormError variant="banner" message={actionError} />
      </Modal>
    );
  }

  return (
    <Modal
      open
      title={groupName}
      description={t('groups.details.title')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.close')}
          </Button>
          <Button variant="danger" disabled={pending} onClick={() => setConfirmDelete(true)}>
            {t('groups.details.delete')}
          </Button>
        </>
      }>
      {status !== 'ready' || !data ? (
        <StateBlock
          status={status === 'error' ? 'error' : 'loading'}
          message={error}
          onRetry={reload}
        />
      ) : (
        <>
          <FormError variant="banner" message={actionError} />
          <h3 className="ui-section-title">{t('groups.details.activity.title')}</h3>
          <DetailList
            rows={[
              {
                label: t('groups.details.activity.members'),
                value: formatCount(data.activity.memberCount),
              },
              {
                label: t('groups.details.activity.newMembers'),
                value: formatCount(data.activity.newMemberCount),
              },
              {
                label: t('groups.details.activity.joinRequests'),
                value: formatCount(data.activity.joinRequestCount),
              },
              {
                label: t('groups.details.activity.postsToday'),
                value: formatCount(data.activity.postCount),
              },
              {
                label: t('groups.details.activity.postsThisMonth'),
                value: formatCount(data.activity.monthPostCount),
              },
              {
                label: t('groups.details.created'),
                value: formatDate(data.createdDate),
              },
              { label: t('groups.details.owner'), value: data.ownerName },
            ]}
          />

          <DetailList
            rows={[
              {
                label: t('groups.columns.privacy'),
                value: (
                  <StatusPill
                    label={t(
                      data.privacyType === 'private'
                        ? 'groups.privacy.private'
                        : 'groups.privacy.public',
                    )}
                    tone={data.privacyType === 'private' ? 'warning' : 'info'}
                  />
                ),
              },
              {
                label: t('groups.columns.members'),
                value: formatCount(data.totalMembers),
              },
              {
                label: t('groups.details.description'),
                value: data.description,
              },
            ]}
          />

          <h3 className="ui-section-title">{t('groups.requests.title')}</h3>
          <DataTable
            caption={t('groups.requests.title')}
            columns={requestColumns}
            rows={data.requests}
            rowKey={request => request.id}
            status="ready"
            refreshing={refreshing}
            emptyTitle={t('groups.requests.emptyTitle')}
            emptyMessage={t('groups.requests.emptyMessage')}
          />

          <h3 className="ui-section-title">{t('groups.members.title')}</h3>
          <DataTable
            caption={t('groups.members.title')}
            columns={memberColumns}
            rows={data.members}
            rowKey={member => member.id}
            status="ready"
            refreshing={refreshing}
            emptyTitle={t('groups.members.emptyTitle')}
            emptyMessage={t('groups.members.emptyMessage')}
          />
        </>
      )}
    </Modal>
  );
}
